import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Account } from '../../../../shared/types/modules/account';
import { Attachment } from '../../../../shared/types/shared';
import { asyncGetAccountByDocument } from '../../../../services/accounts.service';
import { fetchInteractionsAll } from '../../../../services/interactionsHistory.service';
import { asyncSendEmailSending } from '../../../../services/emailSending.service';
import { fileToBase64 } from '../../../../shared/utils/files';
import { ToastSwal } from '../../../components/general/toastSwals/toastSwal';

const MAX_ATTACHMENTS_SIZE_BYTES = 30 * 1024 * 1024;

const validationSchema = Yup.object({
    document: Yup.string().trim().required('El documento es requerido'),
    caseId: Yup.string().required('El caso es requerido'),
    recipients: Yup.array()
        .of(Yup.string().email('Correo inválido'))
        .min(1, 'Al menos un destinatario es requerido')
        .required('Al menos un destinatario es requerido'),
    subject: Yup.string().trim().required('El asunto es requerido'),
    body: Yup.string().required('El cuerpo del correo es requerido'),
});

export const useEmailSending = () => {
    const [account, setAccount] = useState<Account | undefined>();
    const [cases, setCases] = useState<{ id: string; name: string }[]>([]);
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [searching, setSearching] = useState(false);
    const [sending, setSending] = useState(false);

    const formik = useFormik({
        initialValues: {
            document: '',
            caseId: '',
            recipients: [] as string[],
            subject: '',
            body: '',
        },
        validationSchema,
        onSubmit: async (values) => {
            setSending(true);
            try {
                const res = await asyncSendEmailSending({
                    account: account?.id ?? '',
                    caseId: values.caseId,
                    recipients: values.recipients,
                    subject: values.subject,
                    body: values.body,
                    attachments,
                });

                if (res) {
                    ToastSwal.fire({
                        text: 'El correo se envió satisfactoriamente',
                        icon: 'success',
                        timer: 5000,
                        position: 'center-end',
                    });
                    formik.resetForm();
                    setAccount(undefined);
                    setCases([]);
                    setAttachments([]);
                } else {
                    ToastSwal.fire({
                        text: 'No fue posible enviar el correo',
                        icon: 'error',
                        timer: 5000,
                        position: 'center-end',
                    });
                }
            } finally {
                setSending(false);
            }
        },
    });

    const handleSearchAccount = async () => {
        const document = formik.values.document.trim();
        formik.setFieldTouched('document');
        if (!document) return;

        setSearching(true);
        try {
            const result = await asyncGetAccountByDocument(document);
            if (!result) {
                setAccount(undefined);
                setCases([]);
                ToastSwal.fire({
                    text: 'No se encontró una cuenta con ese documento',
                    icon: 'error',
                    timer: 5000,
                    position: 'center-end',
                });
                return;
            }

            setAccount(result);
            formik.setFieldValue('caseId', '');
            if (result.email)
                formik.setFieldValue('recipients', [result.email]);

            const res = await fetchInteractionsAll({
                identification: document,
                limit: 500,
            });
            const rows = res?.items?.data ?? [];
            setCases(
                rows
                    .filter((row) => row.caseId)
                    .map((row) => ({
                        id: row.caseId as string,
                        name: `${row.source} - ${
                            row.typification || row.observation || ''
                        } (${new Date(row.createdAt).toLocaleString()})`,
                    }))
            );
        } finally {
            setSearching(false);
        }
    };

    const handleOnChangeAttachments = async (files: File[]) => {
        const data: Attachment[] = [];
        for await (const file of files) {
            const res = await fileToBase64(file);
            if (res) data.push(res);
        }
        setAttachments(data);
    };

    const handleSubmit = async () => {
        const totalSize = attachments.reduce(
            (total, attachment) =>
                total + (attachment.base64?.length ?? 0) * 0.75,
            0
        );
        if (totalSize > MAX_ATTACHMENTS_SIZE_BYTES) {
            ToastSwal.fire({
                text: 'Los adjuntos superan el tamaño máximo de 30MB',
                icon: 'error',
                timer: 5000,
                position: 'center-end',
            });
            return;
        }

        await formik.submitForm();
    };

    return {
        account,
        cases,
        attachments,
        formik,
        searching,
        sending,
        handleSearchAccount,
        handleOnChangeAttachments,
        handleSubmit,
    };
};